import { useMemo, useState, type ComponentType } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LogoutIcon from '@mui/icons-material/Logout'

import DashboardIcon from '@mui/icons-material/SpaceDashboard'
import BarChartIcon from '@mui/icons-material/BarChart'
import ChecklistIcon from '@mui/icons-material/ChecklistRtl'
import MapIcon from '@mui/icons-material/Public'
import InsightsIcon from '@mui/icons-material/Insights'
import HubIcon from '@mui/icons-material/Hub'
import PaymentsIcon from '@mui/icons-material/Payments'
import NotificationsIcon from '@mui/icons-material/NotificationsNone'
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined'
import BusinessIcon from '@mui/icons-material/BusinessOutlined'
import SyncAltIcon from '@mui/icons-material/SyncAltOutlined'
import HistoryIcon from '@mui/icons-material/HistoryOutlined'
import LanIcon from '@mui/icons-material/LanOutlined'
import SessionIcon from '@mui/icons-material/GroupsOutlined'

import { alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import { useAuth } from '../auth/AuthContext'
import { SB } from '../theme'

// FinanceLens parity: 232px indigo dark-island sidebar. Grouped nav
// with uppercase group labels; groups collapse to headers, the group
// containing the active route auto-expands. Active item: gold gradient
// pill + 2px gold left border.
const WIDTH = 232
const COLLAPSE_KEY = 'fieldlens.sidebar.collapsed.v1'

type NavItem = {
  id: string
  label: string
  to: string
  icon: ComponentType<{ sx?: object }>
  group: string
  adminOnly?: boolean       // requires 'admin' in current active roles
  platformOnly?: boolean    // requires a 'platform_admin' membership anywhere
  end?: boolean             // exact match for `/`
  matchPrefix?: string      // optional extra prefix that also counts as active (admin roll-up)
}

type Group = { key: string; label: string | null; platformOnly?: boolean }

const NAV: NavItem[] = [
  // Platform surface — only visible to users with a platform_admin membership.
  // Kept at the top since a platform admin lands on /platform after login.
  { id: 'platform-tenants',  label: 'Tenants',  to: '/platform/tenants',  icon: LanIcon,     group: 'platform', platformOnly: true, matchPrefix: '/platform/tenants' },
  { id: 'platform-sessions', label: 'Sessions', to: '/platform/sessions', icon: SessionIcon, group: 'platform', platformOnly: true },

  { id: 'control-tower', label: 'Control Tower', to: '/',        icon: DashboardIcon,    group: 'main', end: true },
  { id: 'enterprise',    label: 'Enterprise',    to: '/enterprise', icon: BarChartIcon,  group: 'main' },

  { id: 'readiness',     label: 'Pre-flight Readiness', to: '/readiness',   icon: ChecklistIcon, group: 'ops' },
  { id: 'map',           label: 'Live Map',             to: '/map',         icon: MapIcon,       group: 'ops' },

  { id: 'analytics',     label: 'Exception Analytics',  to: '/analytics',   icon: InsightsIcon,  group: 'intel' },
  { id: 'asset-health',  label: 'Asset Health',         to: '/asset-health',icon: HubIcon,       group: 'intel' },
  { id: 'cost',          label: 'Cost-to-Serve',        to: '/cost',        icon: PaymentsIcon,  group: 'intel' },

  { id: 'notifications', label: 'Notifications',        to: '/notifications-prefs', icon: NotificationsIcon, group: 'setup' },

  { id: 'admin-users',   label: 'Users',           to: '/admin/users',   icon: PeopleIcon,          group: 'admin', adminOnly: true },
  { id: 'admin-sources', label: 'Data sources',    to: '/admin/sources', icon: SyncAltIcon,         group: 'admin', adminOnly: true },
  { id: 'admin-tenant',  label: 'Tenant profile',  to: '/admin/tenant',  icon: BusinessIcon,        group: 'admin', adminOnly: true },
  { id: 'admin-audit',   label: 'Audit trail',     to: '/admin/audit',   icon: HistoryIcon,         group: 'admin', adminOnly: true },
]

const GROUPS: Group[] = [
  { key: 'platform', label: 'PLATFORM', platformOnly: true },
  { key: 'main',     label: null },
  { key: 'ops',      label: 'OPERATIONS' },
  { key: 'intel',    label: 'INTELLIGENCE' },
  { key: 'setup',    label: 'CONFIGURATION' },
  { key: 'admin',    label: 'ADMINISTRATION' },
]

function readCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch { return new Set() }
}
function writeCollapsed(next: Set<string>): void {
  try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(Array.from(next))) } catch { /* ignore */ }
}

// Route resolution: which nav item is active given the current URL?
// Admin routes roll up to their own sidebar entries; other pages exact-match.
function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to
  if (pathname === item.to) return true
  if (item.matchPrefix && pathname.startsWith(item.matchPrefix)) return true
  return false
}

type Props = {
  mobileOpen: boolean
  onMobileClose: () => void
}

export default function Sidebar({ mobileOpen, onMobileClose }: Props) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isAdmin = !!user?.roles.includes('admin')
  // Platform surface visibility follows the membership set — not the active
  // role. A platform admin who switched into a tenant still needs the
  // Platform group to switch back.
  const isPlatformAdmin = !!user?.memberships?.some((m) => m.role === 'platform_admin')
  // When the user's ACTIVE membership is platform, we hide every tenant-
  // scoped surface (Control Tower, Readiness, Map, Analytics, Asset Health,
  // Cost, Configuration, tenant Administration). They belong to no tenant
  // in this session — the queries would return empty anyway. Switching
  // into a tenant via the profile-menu workspace switcher brings them
  // back.
  const activeIsPlatform = !!user?.active?.is_platform

  const items = useMemo(
    () =>
      NAV.filter((i) => {
        if (i.adminOnly && !isAdmin) return false
        if (i.platformOnly && !isPlatformAdmin) return false
        // Platform-active session sees ONLY the platform group.
        if (activeIsPlatform && i.group !== 'platform') return false
        return true
      }),
    [isAdmin, isPlatformAdmin, activeIsPlatform],
  )
  const groupsVisible = useMemo(
    () =>
      GROUPS.filter((g) => {
        if (g.platformOnly && !isPlatformAdmin) return false
        if (activeIsPlatform && g.key !== 'platform') return false
        return true
      }),
    [isPlatformAdmin, activeIsPlatform],
  )

  const [collapsed, setCollapsedState] = useState<Set<string>>(readCollapsed)
  const toggleGroup = (key: string) => {
    setCollapsedState(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      writeCollapsed(next)
      return next
    })
  }

  const initials = (user?.display_name ?? user?.email ?? 'FL')
    .split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()

  const drawerBody = (
    <>
      {/* Brand block: QEA logo chip + product name + tenant */}
      <Box sx={{ px: 2.5, py: 2.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
          background: `linear-gradient(135deg, ${SB.ACCENT} 0%, #D4931A 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: SB.BG_DEEP, fontFamily: '"Inter", sans-serif', letterSpacing: '0.02em' }}>
            QEA
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.15, color: SB.TEXT }}>
            FieldLens
          </Typography>
          <Typography sx={{ color: SB.TEXT_DIM, fontSize: '0.62rem', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {user?.tenant_slug ?? 'insight-delivered'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: SB.BORDER, mx: 1 }} />

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {groupsVisible.map(({ key, label }) => {
          const groupItems = items.filter(i => i.group === key)
          if (groupItems.length === 0) return null
          const containsActive = groupItems.some(i => isItemActive(i, location.pathname))
          const isCollapsed = label !== null && collapsed.has(key) && !containsActive

          return (
            <Box key={key} sx={{ mb: 0.5 }}>
              {label && (
                <GroupHeader label={label} collapsed={isCollapsed} onClick={() => toggleGroup(key)} />
              )}
              {!isCollapsed && (
                <List dense disablePadding>
                  {groupItems.map(item => (
                    <NavRow key={item.id} item={item} active={isItemActive(item, location.pathname)} onNavigate={onMobileClose} />
                  ))}
                </List>
              )}
            </Box>
          )
        })}
      </Box>

      {/* User + logout footer */}
      <Divider sx={{ borderColor: SB.BORDER, mx: 1 }} />
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1, flexShrink: 0,
            background: alpha(SB.ACCENT, 0.18),
            border: `1px solid ${alpha(SB.ACCENT, 0.35)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: SB.ACCENT }}>{initials}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.2, color: SB.TEXT,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user?.display_name ?? user?.email ?? 'Signed in'}
            </Typography>
            <Typography sx={{ color: SB.ACCENT, fontSize: '0.62rem', textTransform: 'capitalize' }}>
              {user?.roles?.[0]?.replace(/_/g, ' ') ?? 'user'}
            </Typography>
          </Box>
          <Tooltip title="Sign out" placement="top">
            <Box
              role="button"
              tabIndex={0}
              aria-label="Sign out"
              onClick={logout}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') logout() }}
              sx={{
                color: SB.TEXT_DIM, cursor: 'pointer', display: 'flex', p: 0.5, borderRadius: 1,
                '&:hover': { color: '#E5501C', bgcolor: alpha('#E5501C', 0.08) },
                '&:focus-visible': { outline: `2px solid ${SB.ACCENT}`, outlineOffset: 1 },
              }}
            >
              <LogoutIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        </Box>
        <Typography sx={{ mt: 1.25, fontSize: '0.58rem', color: SB.TEXT_DIM, textAlign: 'right' }}>
          QEA FieldLens · v0.2.6
        </Typography>
      </Box>
    </>
  )

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: WIDTH,
          boxSizing: 'border-box',
          background: `
            radial-gradient(ellipse at top left, ${alpha(SB.BG_DEEP, 0.95)} 0%, transparent 60%),
            ${SB.BG_MID}
          `,
          color: SB.TEXT,
          border: 'none',
          borderRight: `1px solid ${SB.BORDER}`,
          backgroundImage: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {drawerBody}
      </Box>
    </Drawer>
  )
}

function GroupHeader({ label, collapsed, onClick }: { label: string; collapsed: boolean; onClick: () => void }) {
  const Chevron = collapsed ? ChevronRightIcon : ExpandMoreIcon
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick() }}
      sx={{
        px: 2.5, mt: 1.5, mb: 0.5,
        display: 'flex', alignItems: 'center', gap: 0.5,
        cursor: 'pointer', userSelect: 'none',
        '&:hover .fl-grp-lbl': { color: SB.TEXT },
        '&:focus-visible': { outline: `2px solid ${SB.ACCENT}`, outlineOffset: 2, borderRadius: 1 },
      }}
    >
      <Chevron sx={{ fontSize: 12, color: SB.TEXT_DIM }} />
      <Typography
        className="fl-grp-lbl"
        sx={{ color: SB.TEXT_DIM, fontSize: '0.62rem', lineHeight: 1, fontWeight: 700, letterSpacing: '0.1em' }}
      >
        {label}
      </Typography>
    </Box>
  )
}

function NavRow({ item, active, onNavigate }: { item: NavItem; active: boolean; onNavigate: () => void }) {
  const Icon = item.icon
  return (
    <ListItem disablePadding sx={{ px: 1, mb: 0.25 }}>
      <ListItemButton
        component={NavLink}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        sx={{
          borderRadius: 1.5,
          py: 0.9, px: 1.5,
          background: active
            ? `linear-gradient(90deg, ${alpha(SB.ACCENT, 0.18)} 0%, ${alpha(SB.ACCENT, 0.04)} 100%)`
            : 'transparent',
          borderLeft: `2px solid ${active ? SB.ACCENT : 'transparent'}`,
          '&:hover': { background: 'rgba(255,255,255,0.06)' },
          '&.active': {},
        } as object}
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          <Icon sx={{ fontSize: 17, color: active ? SB.ACCENT : SB.ICON_DIM }} />
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: {
              sx: {
                fontSize: '0.81rem',
                fontWeight: active ? 600 : 500,
                color: active ? SB.TEXT : SB.TEXT_DIM,
              },
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  )
}

// Keep the NavItem/Group types exported in case a downstream file
// (docs, tests) wants to type against the shape.
export type { NavItem, Group }
export { WIDTH as SIDEBAR_WIDTH }
